<template>
  <div class="space-y-6">
    <router-link to="/admin/orders" class="text-blue-600 hover:underline">
      ← Вернуться к заказам
    </router-link>

    <div v-if="loading" class="text-center py-8">
      <p class="text-gray-600">Загрузка заказа...</p>
    </div>

    <div v-else-if="!order" class="bg-white p-8 rounded-lg shadow text-center">
      <p class="text-red-600">Заказ не найден</p>
    </div>

    <div v-else class="space-y-6">
      <div class="flex justify-between items-start">
        <div>
          <h1 class="text-3xl font-bold">{{ order.orderNumber }}</h1>
          <p class="text-gray-600">Создан: {{ formatDate(order.createdAt) }}</p>
        </div>
        <span :class="getStatusClass(order.status)" class="px-6 py-3 rounded-full font-bold text-lg">
          {{ getStatusLabel(order.status) }}
        </span>
      </div>

      <div class="grid grid-cols-2 gap-6">
        <div class="bg-white p-6 rounded-lg shadow">
          <h2 class="text-xl font-bold mb-4">📱 Устройство</h2>
          <div class="space-y-2">
            <p><span class="text-gray-600">Бренд:</span> <strong>{{ order.deviceInfo.brand }}</strong></p>
            <p><span class="text-gray-600">Модель:</span> <strong>{{ order.deviceInfo.model }}</strong></p>
            <div class="mt-4 pt-4 border-t">
              <p class="text-gray-600 text-sm">Проблема:</p>
              <p class="mt-1">{{ order.deviceInfo.issue }}</p>
            </div>
          </div>
        </div>

        <div class="bg-white p-6 rounded-lg shadow">
          <h2 class="text-xl font-bold mb-4">👤 Клиент</h2>
          <div class="space-y-2">
            <p><span class="text-gray-600">Имя:</span> <strong>{{ getCustomerName(order) }}</strong></p>
            <p><span class="text-gray-600">Телефон:</span> <strong>{{ getCustomerPhone(order) }}</strong></p>
            <p><span class="text-gray-600">Канал связи:</span> <strong>{{ getPreferredChannel(order) }}</strong></p>
            <p v-if="getMessengerHandle(order)"><span class="text-gray-600">Telegram:</span> <strong>{{ getMessengerHandle(order) }}</strong></p>
            <p><span class="text-gray-600">Город:</span> <strong>{{ getDeliveryCity(order) }}</strong></p>
            <p><span class="text-gray-600">Адрес:</span> <strong>{{ getCustomerAddress(order) }}</strong></p>
            <p><span class="text-gray-600">Доставка:</span> <strong>{{ getDeliveryMethod(order) }}</strong></p>
            <p v-if="order.deliveryInfo?.senderFullName"><span class="text-gray-600">ПІБ відправника:</span> <strong>{{ order.deliveryInfo.senderFullName }}</strong></p>
          </div>
        </div>
      </div>

      <div class="bg-white p-6 rounded-lg shadow">
        <h2 class="text-xl font-bold mb-4">🔧 Услуги</h2>
        <table class="w-full">
          <thead class="bg-gray-50 border-b">
            <tr>
              <th class="px-4 py-2 text-left text-sm font-bold">Название</th>
              <th class="px-4 py-2 text-left text-sm font-bold">Количество</th>
              <th class="px-4 py-2 text-right text-sm font-bold">Цена</th>
            </tr>
          </thead>
          <tbody class="divide-y">
            <tr v-for="(item, idx) in order.services" :key="idx">
              <td class="px-4 py-3">
                <p class="font-bold">{{ item.service?.name || item.serviceName || 'Послуга' }}</p>
                <p class="text-sm text-gray-600">{{ item.service?.description || 'Запасная позиция без каталога' }}</p>
              </td>
              <td class="px-4 py-3">{{ item.quantity || 1 }}</td>
              <td class="px-4 py-3 text-right font-bold">₴{{ item.price }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="grid grid-cols-2 gap-6">
        <div class="bg-white p-6 rounded-lg shadow">
          <h2 class="text-xl font-bold mb-4">📊 Статус заказа</h2>
          <div class="space-y-3">
            <div>
              <label class="block text-sm font-bold mb-2">Изменить статус</label>
              <select v-model="newStatus" class="w-full border px-3 py-2 rounded">
                <option :value="order.status" disabled>{{ getStatusLabel(order.status) }} (текущий)</option>
                <option value="pending">Ожидание</option>
                <option value="accepted">Принят</option>
                <option value="in-progress">В работе</option>
                <option value="completed">Завершен</option>
                <option value="cancelled">Отменен</option>
              </select>
            </div>
            <button
              @click="updateStatus"
              :disabled="newStatus === order.status || statusLoading"
              class="w-full bg-blue-600 text-white py-2 rounded font-bold hover:bg-blue-700 disabled:opacity-50"
            >
              {{ statusLoading ? 'Обновление...' : 'Обновить статус' }}
            </button>
          </div>
        </div>

        <div class="bg-white p-6 rounded-lg shadow">
          <h2 class="text-xl font-bold mb-4">💳 Оплата</h2>
          <div class="space-y-3">
            <div>
              <p class="text-gray-600 text-sm">Статус платежа</p>
              <span :class="getPaymentClass(order.paymentStatus)" class="px-3 py-1 rounded-full text-sm font-bold inline-block mt-1">
                {{ getPaymentLabel(order.paymentStatus) }}
              </span>
            </div>
            <div>
              <p class="text-gray-600 text-sm">Сумма к оплате</p>
              <p class="text-3xl font-bold text-blue-600">₴{{ order.totalPrice }}</p>
            </div>
            <div>
              <p class="text-gray-600 text-sm">Способ оплаты</p>
              <p class="font-bold">{{ order.paymentMethod || 'На месте' }}</p>
            </div>
            <div class="pt-3 border-t">
              <label class="block text-sm font-bold mb-2">Отметить как оплачено</label>
              <button
                v-if="order.paymentStatus !== 'paid'"
                @click="markAsPaid"
                :disabled="paymentLoading"
                class="w-full bg-green-600 text-white py-2 rounded font-bold hover:bg-green-700 disabled:opacity-50"
              >
                {{ paymentLoading ? 'Обновление...' : '✓ Оплачено' }}
              </button>
              <p v-else class="text-green-600 font-bold">✓ Уже оплачено</p>
            </div>
          </div>
        </div>
      </div>

      <div class="bg-white p-6 rounded-lg shadow">
        <h2 class="text-xl font-bold mb-4">📝 Примечания</h2>
        <textarea
          v-model="notes"
          placeholder="Добавьте примечания для внутреннего использования"
          class="w-full border px-3 py-2 rounded focus:outline-none focus:border-blue-500"
          rows="4"
        ></textarea>
        <button
          @click="saveNotes"
          :disabled="notesLoading"
          class="mt-3 bg-blue-600 text-white px-6 py-2 rounded font-bold hover:bg-blue-700 disabled:opacity-50"
        >
          {{ notesLoading ? 'Сохранение...' : 'Сохранить примечания' }}
        </button>
      </div>

      <div class="bg-white p-6 rounded-lg shadow">
        <h2 class="text-xl font-bold mb-4">⏱️ График выполнения</h2>
        <div class="space-y-3">
          <div v-if="order.startDate" class="flex justify-between">
            <span class="text-gray-600">Начало работы:</span>
            <strong>{{ formatDate(order.startDate) }}</strong>
          </div>
          <div v-if="order.estimatedCompletionDate" class="flex justify-between">
            <span class="text-gray-600">Ожидаемое завершение:</span>
            <strong>{{ formatDate(order.estimatedCompletionDate) }}</strong>
          </div>
          <div v-if="order.completionDate" class="flex justify-between text-green-600">
            <span>Завершено:</span>
            <strong>{{ formatDate(order.completionDate) }}</strong>
          </div>
        </div>
      </div>

      <div class="flex gap-2">
        <router-link
          to="/admin/orders"
          class="flex-1 bg-gray-600 text-white py-3 rounded font-bold hover:bg-gray-700 text-center"
        >
          ← Вернуться к заказам
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useAuthStore } from '../../stores/auth';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { uk } from 'date-fns/locale';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

export default {
  name: 'AdminOrderDetail',
  setup() {
    const route = useRoute();
    const authStore = useAuthStore();
    const order = ref(null);
    const loading = ref(false);
    const newStatus = ref('');
    const statusLoading = ref(false);
    const paymentLoading = ref(false);
    const notesLoading = ref(false);
    const notes = ref('');

    const statusLabels = {
      pending: 'Ожидание',
      accepted: 'Принят',
      'in-progress': 'В работе',
      completed: 'Завершен',
      cancelled: 'Отменен'
    };

    const paymentStatusLabels = {
      unpaid: 'Не оплачен',
      paid: 'Оплачен',
      partial: 'Частично'
    };

    const loadOrder = async () => {
      loading.value = true;
      try {
        const response = await axios.get(`${API_URL}/orders/${route.params.id}`, {
          headers: {
            Authorization: `Bearer ${authStore.token}`
          }
        });
        order.value = response.data;
        newStatus.value = order.value.status;
        notes.value = order.value.internalNotes || '';
      } catch (error) {
        console.error('Ошибка при загрузке заказа:', error);
      } finally {
        loading.value = false;
      }
    };

    const updateStatus = async () => {
      statusLoading.value = true;
      try {
        await axios.patch(
          `${API_URL}/orders/${route.params.id}/status`,
          { status: newStatus.value },
          {
            headers: {
              Authorization: `Bearer ${authStore.token}`
            }
          }
        );
        await loadOrder();
        alert('Статус обновлен');
      } catch (error) {
        alert('Ошибка при обновлении статуса');
      } finally {
        statusLoading.value = false;
      }
    };

    const markAsPaid = async () => {
      paymentLoading.value = true;
      try {
        await axios.patch(
          `${API_URL}/orders/${route.params.id}/status`,
          { paymentStatus: 'paid' },
          {
            headers: {
              Authorization: `Bearer ${authStore.token}`
            }
          }
        );
        await loadOrder();
        alert('Заказ отмечен как оплаченный');
      } catch (error) {
        alert('Ошибка при обновлении статуса платежа');
      } finally {
        paymentLoading.value = false;
      }
    };

    const saveNotes = async () => {
      notesLoading.value = true;
      try {
        await axios.patch(
          `${API_URL}/orders/${route.params.id}/status`,
          { internalNotes: notes.value },
          {
            headers: {
              Authorization: `Bearer ${authStore.token}`
            }
          }
        );
        alert('Примечания сохранены');
      } catch (error) {
        alert('Ошибка при сохранении примечаний');
      } finally {
        notesLoading.value = false;
      }
    };

    const formatDate = (date) => {
      if (!date) return 'Не указано';
      return formatDistanceToNow(new Date(date), { addSuffix: true, locale: uk });
    };

    const getCustomerName = (order) => order.user?.name || order.contactInfo?.name || 'Гость';
    const getCustomerPhone = (order) => order.user?.phone || order.contactInfo?.phone || 'Не указано';
    const getCustomerAddress = (order) => order.user?.address || order.contactInfo?.address || 'Не указано';
    const getPreferredChannel = (order) => ({
      call: 'Подзвонити',
      telegram: 'Telegram',
      viber: 'Viber',
      whatsapp: 'WhatsApp'
    }[order.contactInfo?.preferredChannel] || 'Подзвонити');
    const getMessengerHandle = (order) => order.contactInfo?.messengerHandle || '';
    const getDeliveryCity = (order) => order.deliveryInfo?.city || order.user?.city || 'Не указано';
    const getDeliveryMethod = (order) => ({
      pickup: 'Самовивіз',
      'nova-poshta-branch': 'Нова пошта, відділення',
      'nova-poshta-courier': "Нова пошта, кур'єр"
    }[order.deliveryInfo?.method] || 'Не указано');
    const getStatusLabel = (status) => statusLabels[status] || status;
    const getStatusClass = (status) => {
      const classes = {
        pending: 'bg-yellow-100 text-yellow-700',
        accepted: 'bg-blue-100 text-blue-700',
        'in-progress': 'bg-purple-100 text-purple-700',
        completed: 'bg-green-100 text-green-700',
        cancelled: 'bg-red-100 text-red-700'
      };
      return classes[status] || 'bg-gray-100 text-gray-700';
    };

    const getPaymentLabel = (status) => paymentStatusLabels[status] || status;
    const getPaymentClass = (status) => {
      const classes = {
        unpaid: 'bg-red-100 text-red-700',
        paid: 'bg-green-100 text-green-700',
        partial: 'bg-yellow-100 text-yellow-700'
      };
      return classes[status] || 'bg-gray-100 text-gray-700';
    };

    onMounted(() => {
      loadOrder();
    });

    return {
      order,
      loading,
      newStatus,
      statusLoading,
      paymentLoading,
      notesLoading,
      notes,
      updateStatus,
      markAsPaid,
      saveNotes,
      formatDate,
      getCustomerName,
      getCustomerPhone,
      getCustomerAddress,
      getPreferredChannel,
      getMessengerHandle,
      getDeliveryCity,
      getDeliveryMethod,
      getStatusLabel,
      getStatusClass,
      getPaymentLabel,
      getPaymentClass
    };
  }
};
</script>
